// =============================================================
// useJsonLd —— JSON-LD 结构化数据构建器
// 面向生成式引擎优化（GEO），输出 schema.org 结构化数据，
// 帮助 Google / Bing / 生成式 AI 引擎正确理解站内实体：
//   - Organization / LocalBusiness
//   - WebSite + SearchAction
//   - Product（标准版 / Nova）
//   - FAQPage
//   - BreadcrumbList
// =============================================================
import { SITE } from '@site/data/seo'
import { company } from '@site/data/company'
import { CONTACT } from '@site/data/contact'
import { products } from '@site/data/products'

const SITE_URL = SITE.url
const ORGANIZATION_NAME = company.name

function localBusiness() {
  return {
    '@type': ['Organization', 'LocalBusiness'],
    '@id': `${SITE_URL}/#organization`,
    name: ORGANIZATION_NAME,
    alternateName: SITE.name,
    url: SITE_URL,
    logo: `${SITE_URL}${SITE.ogImage}`,
    description: company.intro,
    telephone: CONTACT.phone1.tel,
    address: {
      '@type': 'PostalAddress',
      streetAddress: CONTACT.address,
      addressLocality: '丹阳市',
      addressRegion: '江苏省',
      addressCountry: 'CN',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '09:00',
        closes: '22:00',
      },
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: CONTACT.phone1.tel,
      contactType: 'sales',
      availableLanguage: 'zh-CN',
    },
  }
}

function webSite() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE.name,
    description: SITE.description,
    inLanguage: 'zh-CN',
    publisher: { '@id': `${SITE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

function product(product, productUrl) {
  return {
    '@type': 'Product',
    name: product.name,
    description: product.tagline,
    url: `${SITE_URL}${productUrl}`,
    brand: { '@type': 'Brand', name: SITE.name },
    manufacturer: { '@id': `${SITE_URL}/#organization` },
    image: `${SITE_URL}${SITE.ogImage}`,
    ...(product.isNew ? { additionalProperty: { '@type': 'PropertyValue', name: '新品上市', value: product.launchDate } } : {}),
  }
}

function breadcrumb(items) {
  if (!items || items.length === 0) return null
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

// 各路由对应的 JSON-LD 集合
function schemaForRoute(route) {
  const name = route.name

  switch (name) {
    case 'product-standard':
      return {
        product: product(products[0], '/product/standard'),
        breadcrumb: breadcrumb([
          { name: '首页', url: `${SITE_URL}/` },
          { name: '航富通标准版', url: `${SITE_URL}/product/standard` },
        ]),
      }
    case 'product-nova':
      return {
        product: product(products[1], '/product/nova'),
        breadcrumb: breadcrumb([
          { name: '首页', url: `${SITE_URL}/` },
          { name: '航富通 Nova', url: `${SITE_URL}/product/nova` },
        ]),
      }
    case 'compare':
      return {
        breadcrumb: breadcrumb([
          { name: '首页', url: `${SITE_URL}/` },
          { name: '版本对比', url: `${SITE_URL}/compare` },
        ]),
      }
    case 'about':
      return {
        localBusiness: localBusiness(),
        breadcrumb: breadcrumb([
          { name: '首页', url: `${SITE_URL}/` },
          { name: '关于我们', url: `${SITE_URL}/about` },
        ]),
      }
    default:
      // home
      return {
        breadcrumb: breadcrumb([{ name: '首页', url: `${SITE_URL}/` }]),
      }
  }
}

// 输出：全局实体 + 页面专属实体
export function buildJsonLd(route) {
  const routeSchemas = schemaForRoute(route)
  const schemas = []

  // 组织实体：首页/关于页用 LocalBusiness（含地址），其他页用精简 Organization
  const includeLocal = route.name === 'home' || route.name === 'about'
  schemas.push(includeLocal ? localBusiness() : { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: ORGANIZATION_NAME, url: SITE_URL, logo: `${SITE_URL}${SITE.ogImage}` })
  schemas.push(webSite())
  if (routeSchemas.product) schemas.push(routeSchemas.product)
  if (routeSchemas.breadcrumb) schemas.push(routeSchemas.breadcrumb)

  return schemas
}



// WEBPACK FOOTER //
// ./src/site/composables/useJsonLd.js